<template>
  <div class="pools-interface">
    <!-- Cabeçalho com título e botões de ação -->
    <div class="d-flex justify-space-between align-center mb-6">
      <h2 class="text-h5">Pools de Liquidez</h2>
      <v-btn
        color="primary"
        prepend-icon="mdi-plus"
        @click="showAddLiquidityDialog = true"
      >
        Adicionar Liquidez
      </v-btn>
    </div>

    <!-- Estatísticas gerais -->
    <v-row class="mb-6">
      <v-col cols="12" md="4">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Total em Pools</div>
          <div class="text-h5 font-weight-bold mb-2">$4,586,245.32</div>
          <div class="text-caption text-success">+5.2% nas últimas 24h</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Sua Liquidez</div>
          <div class="text-h5 font-weight-bold mb-2">$12,842.75</div>
          <div class="text-caption">Em 3 pools diferentes</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Recompensas Pendentes</div>
          <div class="text-h5 font-weight-bold mb-2">24.5 {{ coin }}</div>
          <div class="text-caption">≈ $61.25</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Abas para Pools e Calculadora -->
    <v-tabs v-model="activeTab" class="mb-6">
      <v-tab value="pools">Todas as Pools</v-tab>
      <v-tab value="my-pools">Minhas Pools</v-tab>
      <v-tab value="calculator">
        <v-icon start>mdi-calculator</v-icon>
        Calculadora de IL
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab" class="mb-6">
      <!-- Tab de todas as pools -->
      <v-window-item value="pools">
        <!-- Filtros e pesquisa -->
        <div class="d-flex flex-wrap align-center gap-4 mb-4">
      <v-text-field
        v-model="searchQuery"
        label="Buscar pool"
        density="compact"
        variant="outlined"
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="max-width-300"
        clearable
      ></v-text-field>

      <v-select
        v-model="filterNetwork"
        :items="networks"
        item-title="name"
        item-value="id"
        label="Rede"
        density="compact"
        variant="outlined"
        hide-details
        class="max-width-200"
      >
        <template v-slot:selection="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="20" :color="item.raw.color" class="mr-2">
              <span class="text-caption">{{ item.raw.icon }}</span>
            </v-avatar>
            {{ item.raw.name }}
          </div>
        </template>
      </v-select>

      <v-checkbox
        v-model="showMyPoolsOnly"
        label="Apenas minhas pools"
        hide-details
        density="compact"
      ></v-checkbox>
    </div>

    <!-- Lista de pools disponíveis -->
    <v-card>
      <v-data-table
        :headers="poolHeaders"
        :items="filteredPools"
        class="pools-table"
        :loading="isLoading"
        @click:row="showPoolDetails"
      >
        <!-- Coluna par de tokens -->
        <template v-slot:item.pair="{ item }">
          <div class="d-flex align-center">
            <div class="token-pair-icons position-relative mr-2">
              <v-avatar size="24" :color="item.raw.token1.color" class="token-icon">
                <span class="text-caption text-white">{{ item.raw.token1.symbol.charAt(0) }}</span>
              </v-avatar>
              <v-avatar size="24" :color="item.raw.token2.color" class="token-icon token-icon-second">
                <span class="text-caption text-white">{{ item.raw.token2.symbol.charAt(0) }}</span>
              </v-avatar>
            </div>
            <span>{{ item.raw.token1.symbol }}/{{ item.raw.token2.symbol }}</span>
          </div>
        </template>

        <!-- Coluna rede -->
        <template v-slot:item.network="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="20" :color="getNetworkColor(item.raw.network)" class="mr-2">
              <span class="text-caption text-white">{{ getNetworkIcon(item.raw.network) }}</span>
            </v-avatar>
            <span>{{ getNetworkName(item.raw.network) }}</span>
          </div>
        </template>

        <!-- Coluna APR -->
        <template v-slot:item.apr="{ item }">
          <span :class="item.raw.apr >= 15 ? 'text-success font-weight-bold' : ''">{{ item.raw.apr }}%</span>
        </template>

        <!-- Coluna participação -->
        <template v-slot:item.userParticipation="{ item }">
          <div v-if="item.raw.userParticipation.hasLiquidity">
            <div class="d-flex align-center">
              <span class="font-weight-medium">${{ formatNumber(item.raw.userParticipation.valueUsd) }}</span>
            </div>
            <div class="text-caption">
              {{ formatNumber(item.raw.userParticipation.token1Amount) }} {{ item.raw.token1.symbol }} +
              {{ formatNumber(item.raw.userParticipation.token2Amount) }} {{ item.raw.token2.symbol }}
            </div>
          </div>
          <span v-else class="text-caption">—</span>
        </template>

        <!-- Coluna ações -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-btn
              variant="text"
              color="primary"
              size="small"
              icon="mdi-plus"
              @click="openAddLiquidityDialog(item.raw)"
              class="mr-1"
            ></v-btn>
            <v-btn
              variant="text"
              color="error"
              size="small"
              icon="mdi-minus"
              @click="openRemoveLiquidityDialog(item.raw)"
              :disabled="!item.raw.userParticipation.hasLiquidity"
              class="mr-1"
            ></v-btn>
            <v-btn
              variant="text"
              color="success"
              size="small"
              icon="mdi-cash-multiple"
              @click="claimRewards(item.raw)"
              :disabled="item.raw.pendingRewards <= 0"
            ></v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>
      </v-window-item>
      
      <!-- Tab das minhas pools -->
      <v-window-item value="my-pools">
        <div v-if="filteredUserPools.length > 0">
          <v-card>
            <v-data-table
              :headers="poolHeaders"
              :items="filteredUserPools"
              class="pools-table"
              :loading="isLoading"
            >
              <!-- Usar os mesmos templates da tabela principal -->
              <template v-slot:item.pair="{ item }">
                <div class="d-flex align-center">
                  <div class="token-pair-icons position-relative mr-2">
                    <v-avatar size="24" :color="item.raw.token1.color" class="token-icon">
                      <span class="text-caption text-white">{{ item.raw.token1.symbol.charAt(0) }}</span>
                    </v-avatar>
                    <v-avatar size="24" :color="item.raw.token2.color" class="token-icon token-icon-second">
                      <span class="text-caption text-white">{{ item.raw.token2.symbol.charAt(0) }}</span>
                    </v-avatar>
                  </div>
                  <span>{{ item.raw.token1.symbol }}/{{ item.raw.token2.symbol }}</span>
                </div>
              </template>

              <template v-slot:item.network="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="20" :color="getNetworkColor(item.raw.network)" class="mr-2">
                    <span class="text-caption text-white">{{ getNetworkIcon(item.raw.network) }}</span>
                  </v-avatar>
                  <span>{{ getNetworkName(item.raw.network) }}</span>
                </div>
              </template>

              <template v-slot:item.apr="{ item }">
                <span :class="item.raw.apr >= 15 ? 'text-success font-weight-bold' : ''">{{ item.raw.apr }}%</span>
              </template>

              <template v-slot:item.userParticipation="{ item }">
                <div v-if="item.raw.userParticipation.hasLiquidity">
                  <div class="d-flex align-center">
                    <span class="font-weight-medium">${{ formatNumber(item.raw.userParticipation.valueUsd) }}</span>
                  </div>
                  <div class="text-caption">
                    {{ formatNumber(item.raw.userParticipation.token1Amount) }} {{ item.raw.token1.symbol }} +
                    {{ formatNumber(item.raw.userParticipation.token2Amount) }} {{ item.raw.token2.symbol }}
                  </div>
                </div>
                <span v-else class="text-caption">—</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="d-flex">
                  <v-btn
                    variant="text"
                    color="primary"
                    size="small"
                    icon="mdi-plus"
                    @click="openAddLiquidityDialog(item.raw)"
                    class="mr-1"
                  ></v-btn>
                  <v-btn
                    variant="text"
                    color="error"
                    size="small"
                    icon="mdi-minus"
                    @click="openRemoveLiquidityDialog(item.raw)"
                    :disabled="!item.raw.userParticipation.hasLiquidity"
                    class="mr-1"
                  ></v-btn>
                  <v-btn
                    variant="text"
                    color="success"
                    size="small"
                    icon="mdi-cash-multiple"
                    @click="claimRewards(item.raw)"
                    :disabled="item.raw.pendingRewards <= 0"
                  ></v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </div>
        <v-card v-else class="pa-6 text-center">
          <v-icon color="grey" size="64" class="mb-4">mdi-pool</v-icon>
          <h3 class="text-h5 mb-2">Você ainda não possui liquidez em nenhum pool</h3>
          <p class="text-body-1 mb-4">Adicione liquidez a um pool para começar a ganhar taxas e recompensas.</p>
          <v-btn color="primary" @click="showAddLiquidityDialog = true">{{ t('dex.addLiquidity') }}</v-btn>
        </v-card>
      </v-window-item>
      
      <!-- Tab da calculadora de impermanent loss -->
      <v-window-item value="calculator">
        <ImpermanentLossCalculator />
      </v-window-item>
    </v-window>
    
    <!-- Diálogo de detalhes da pool -->
    <v-dialog
      v-model="poolDetailsDialog.show"
      max-width="1000"
      scrollable
    >
      <v-card v-if="poolDetailsDialog.pool">
        <v-card-title class="d-flex align-center">
          <div class="d-flex align-center">
            <div class="token-pair-icons position-relative mr-2">
              <v-avatar size="28" :color="poolDetailsDialog.pool.token1.color" class="token-icon">
                <span class="text-caption text-white">{{ poolDetailsDialog.pool.token1.symbol.charAt(0) }}</span>
              </v-avatar>
              <v-avatar size="28" :color="poolDetailsDialog.pool.token2.color" class="token-icon token-icon-second">
                <span class="text-caption text-white">{{ poolDetailsDialog.pool.token2.symbol.charAt(0) }}</span>
              </v-avatar>
            </div>
            <span class="text-h6">{{ poolDetailsDialog.pool.token1.symbol }}/{{ poolDetailsDialog.pool.token2.symbol }}</span>
          </div>
          <v-chip
            :color="getNetworkColor(poolDetailsDialog.pool.network)"
            size="small"
            class="text-uppercase ml-4"
          >
            <v-avatar start>
              <span>{{ getNetworkIcon(poolDetailsDialog.pool.network) }}</span>
            </v-avatar>
            {{ getNetworkName(poolDetailsDialog.pool.network) }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="poolDetailsDialog.show = false"></v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <!-- Informações básicas da pool -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="3">
              <div class="info-block">
                <div class="text-overline mb-1">APR Total</div>
                <div class="text-h5 font-weight-bold">{{ poolDetailsDialog.pool.apr }}%</div>
                <div class="text-caption d-flex align-center">
                  <span>Taxa: {{ (poolDetailsDialog.pool.volume24h * 0.0025 / poolDetailsDialog.pool.tvl * 100).toFixed(1) }}%</span>
                  <v-tooltip text="Recompensas de taxas baseadas no volume das últimas 24h">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="3">
              <div class="info-block">
                <div class="text-overline mb-1">TVL</div>
                <div class="text-h5 font-weight-bold">${{ formatNumber(poolDetailsDialog.pool.tvl) }}</div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="3">
              <div class="info-block">
                <div class="text-overline mb-1">Volume 24h</div>
                <div class="text-h5 font-weight-bold">${{ formatNumber(poolDetailsDialog.pool.volume24h) }}</div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="3">
              <div class="info-block">
                <div class="text-overline mb-1">Sua Participação</div>
                <div v-if="poolDetailsDialog.pool.userParticipation.hasLiquidity" class="text-h5 font-weight-bold">
                  ${{ formatNumber(poolDetailsDialog.pool.userParticipation.valueUsd) }}
                </div>
                <div v-else class="text-h5 text-grey">
                  $0.00
                </div>
              </div>
            </v-col>
          </v-row>
          
          <!-- Botões de ação -->
          <div class="d-flex gap-2 mb-6">
            <v-btn 
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddLiquidityDialog(poolDetailsDialog.pool)"
            >
              Adicionar Liquidez
            </v-btn>
            
            <v-btn
              color="error"
              prepend-icon="mdi-minus"
              @click="openRemoveLiquidityDialog(poolDetailsDialog.pool)"
              :disabled="!poolDetailsDialog.pool.userParticipation.hasLiquidity"
            >
              Remover Liquidez
            </v-btn>
            
            <v-btn
              color="success"
              prepend-icon="mdi-cash-multiple"
              @click="claimRewards(poolDetailsDialog.pool)"
              :disabled="poolDetailsDialog.pool.pendingRewards <= 0"
            >
              Reclamar {{ formatNumber(poolDetailsDialog.pool.pendingRewards) }} {{ coin }}
            </v-btn>
          </div>
          
          <!-- Gráficos de desempenho -->
          <PoolPerformanceChart 
            :poolId="poolDetailsDialog.pool.id" 
            :token1="poolDetailsDialog.pool.token1.symbol"
            :token2="poolDetailsDialog.pool.token2.symbol"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo para adicionar liquidez -->
    <v-dialog
      v-model="showAddLiquidityDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-chart-bubble</v-icon>
          Adicionar Liquidez
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="showAddLiquidityDialog = false"></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-center my-4" v-if="!selectedPool">
            <p>Selecione uma pool para adicionar liquidez ou crie uma nova</p>
          </div>
          <div v-else>
            <!-- Conteúdo do diálogo de adicionar liquidez -->
            <p class="text-body-1 mb-4">Adicione liquidez para o par {{ selectedPool.token1.symbol }}/{{ selectedPool.token2.symbol }} e receba taxas de transação e recompensas de staking.</p>
            
            <!-- Implementação básica, será expandida no futuro -->
            <div class="text-center my-8">
              <v-icon size="64" color="primary">mdi-chart-bubble</v-icon>
              <p class="mt-4">Funcionalidade em desenvolvimento</p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'
import ImpermanentLossCalculator from './ImpermanentLossCalculator.vue'
import PoolPerformanceChart from './PoolPerformanceChart.vue'

export default {
  name: 'PoolsInterface',
  components: {
    ImpermanentLossCalculator,
    PoolPerformanceChart
  },
  data() {
    return {
      activeTab: 'pools',
      isLoading: false,
      searchQuery: '',
      filterNetwork: null,
      showMyPoolsOnly: false,
      showAddLiquidityDialog: false,
      selectedPool: null,
      poolDetailsDialog: {
        show: false,
        pool: null
      },
      networks: [
        { id: null, name: 'Todas as redes', color: 'grey', icon: 'T' },
        { id: 'quarnx', name: 'QuarnX', color: 'primary', icon: 'Q' },
        { id: 'ethereum', name: 'Ethereum', color: '#6B7FFF', icon: 'E' },
        { id: 'bsc', name: 'BSC', color: '#F0B90B', icon: 'B' },
        { id: 'polkadot', name: 'Polkadot', color: '#E6007A', icon: 'P' }
      ],
      poolHeaders: [
        { title: 'Par', key: 'pair', align: 'start' },
        { title: 'Rede', key: 'network', align: 'start' },
        { title: 'TVL', key: 'tvl', align: 'end' },
        { title: 'Volume 24h', key: 'volume24h', align: 'end' },
        { title: 'APR', key: 'apr', align: 'end' },
        { title: 'Sua participação', key: 'userParticipation', align: 'end' },
        { title: 'Recompensas', key: 'pendingRewards', align: 'end' },
        { title: 'Ações', key: 'actions', align: 'center', sortable: false }
      ],
      pools: [
        {
          id: 1,
          token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
          token2: { symbol: 'QUSD', color: '#6BBF4B' },
          network: 'quarnx',
          tvl: 2450750.25,
          volume24h: 786543.12,
          apr: 24.5,
          pendingRewards: 12.75,
          userParticipation: {
            hasLiquidity: true,
            valueUsd: 8250.45,
            token1Amount: 3300.5,
            token2Amount: 3300.5
          }
        },
        {
          id: 2,
          token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
          token2: { symbol: 'ETH', color: '#6B7FFF' },
          network: 'quarnx',
          tvl: 950235.75,
          volume24h: 325640.50,
          apr: 18.2,
          pendingRewards: 8.5,
          userParticipation: {
            hasLiquidity: true,
            valueUsd: 4592.30,
            token1Amount: 1840.25,
            token2Amount: 0.92
          }
        },
        {
          id: 3,
          token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
          token2: { symbol: 'BNB', color: '#F0B90B' },
          network: 'quarnx',
          tvl: 782560.80,
          volume24h: 245680.30,
          apr: 16.8,
          pendingRewards: 3.25,
          userParticipation: {
            hasLiquidity: false,
            valueUsd: 0,
            token1Amount: 0,
            token2Amount: 0
          }
        },
        {
          id: 4,
          token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
          token2: { symbol: 'DOT', color: '#E6007A' },
          network: 'quarnx',
          tvl: 402698.52,
          volume24h: 96750.85,
          apr: 14.5,
          pendingRewards: 0,
          userParticipation: {
            hasLiquidity: false,
            valueUsd: 0,
            token1Amount: 0,
            token2Amount: 0
          }
        }
      ]
    }
  },
  computed: {
    dexStore() {
      return useDexStore()
    },
    appStore() {
      return useAppStore()
    },
    filteredPools() {
      let result = [...this.pools]
      
      // Filtrar por rede
      if (this.filterNetwork) {
        result = result.filter(pool => pool.network === this.filterNetwork)
      }
      
      // Filtrar pela consulta de pesquisa
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(pool => 
          pool.token1.symbol.toLowerCase().includes(query) || 
          pool.token2.symbol.toLowerCase().includes(query)
        )
      }
      
      // Filtrar apenas minhas pools
      if (this.showMyPoolsOnly) {
        result = result.filter(pool => pool.userParticipation.hasLiquidity)
      }
      
      return result
    },
    filteredUserPools() {
      return this.pools.filter(pool => pool.userParticipation.hasLiquidity)
    }
  },
  methods: {
    formatNumber(value, decimalPlaces = 2) {
      if (value === undefined || value === null) return '0'
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      })
    },
    getNetworkColor(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.color : 'grey'
    },
    getNetworkName(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.name : 'Desconhecida'
    },
    getNetworkIcon(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.icon : '?'
    },
    openAddLiquidityDialog(pool) {
      this.selectedPool = pool
      this.showAddLiquidityDialog = true
      if (this.poolDetailsDialog.show) {
        this.poolDetailsDialog.show = false
      }
    },
    showPoolDetails(item) {
      // Mostrar detalhes da pool selecionada
      this.poolDetailsDialog.pool = item.raw
      this.poolDetailsDialog.show = true
    },
    
    openRemoveLiquidityDialog(pool) {
      // Implementação futura
      if (this.poolDetailsDialog.show) {
        this.poolDetailsDialog.show = false
      }
      this.appStore.showSnackbar({
        text: `Remoção de liquidez para ${pool.token1.symbol}/${pool.token2.symbol} em desenvolvimento`,
        color: 'info'
      })
    },
    claimRewards(pool) {
      // Simulação de reivindicação de recompensas
      this.isLoading = true
      
      setTimeout(() => {
        this.isLoading = false
        this.appStore.showSnackbar({
          text: `${pool.pendingRewards} ${pool.token1.symbol} reivindicados com sucesso`,
          color: 'success'
        })
        
        // Atualiza o pool para zerar as recompensas pendentes
        pool.pendingRewards = 0
      }, 1500)
    }
  },
  mounted() {
    // Simula carregamento de dados
    this.isLoading = true
    setTimeout(() => {
      this.isLoading = false
    }, 800)
  }
}
</script>

<style scoped>
.pools-interface {
  width: 100%;
}

.token-icon {
  z-index: 1;
}

.token-icon-second {
  margin-left: -12px;
}

.max-width-300 {
  max-width: 300px;
}

.max-width-200 {
  max-width: 200px;
}

.info-block {
  padding: 12px;
  border-radius: 8px;
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
  height: 100%;
}

.gap-2 {
  gap: 8px;
}

.border-top {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.pools-table {
  cursor: pointer;
}

.pools-table {
  border-radius: 8px;
  overflow: hidden;
}

.token-pair-icons {
  width: 32px;
  height: 24px;
}

.token-icon {
  border: 1px solid white;
}

.token-icon-second {
  position: absolute;
  left: 12px;
  top: 0;
}

.gap-4 {
  gap: 16px;
}

.h-100 {
  height: 100%;
}
</style>
